import { fetchApi } from './api';
import type { PresignHomeworkPhotoInput } from '@repo/validation';

export type DocType =
  | 'CSV_CLIENTS'
  | 'RECEIPT'
  | 'BANK_TRANSFER_SCREENSHOT'
  | 'BUSINESS_CARD'
  | 'INVOICE_TEMPLATE'
  | 'ORG_LOGO'
  | 'OTHER';

export interface PresignParams {
  fileName: string;
  fileType: string;
  fileSize: number;
  docType: DocType;
  linkedThreadId?: string;
}

export interface PresignResponse {
  uploadUrl: string;
  documentId: string;
  /** Set only for public document types (ORG_LOGO): the CDN URL to store.
   *  Private documents get null (THI-198) — the server signs its own read
   *  URL whenever it needs the file; the client only ever keeps `documentId`. */
  fileUrl: string | null;
  fileKey: string;
}

export async function presignUpload(params: PresignParams): Promise<PresignResponse> {
  return fetchApi<PresignResponse>('/upload/presign', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export function uploadToR2(
  uploadUrl: string,
  file: Blob,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: HTTP ${xhr.status}`));
    });
    xhr.addEventListener('error', () => reject(new Error('Upload network error')));
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
}

/** THI-196: one presigned PUT for one homework photo. The key is minted by
 *  the server under `homework/<orgId>/<sessionId>/`; the client never
 *  proposes one. A 403 FEATURE_LOCKED or 409 PHOTO_LIMIT surfaces as the
 *  thrown error's `code`. */
export async function presignHomeworkPhoto(
  sessionId: string,
  body: PresignHomeworkPhotoInput,
): Promise<{ photoId: string; key: string; uploadUrl: string }> {
  return fetchApi<{ photoId: string; key: string; uploadUrl: string }>(`/sessions/${sessionId}/homework-photos/presign`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** `uploadToR2` with the one retry trap 9 asks for: a 403 on the PUT (the
 *  presigned URL aged out on a slow connection) re-presigns once. */
export async function uploadWithRepresign(
  file: Blob,
  presign: () => Promise<{ uploadUrl: string; key: string; photoId: string }>,
  onProgress?: (pct: number) => void,
): Promise<{ key: string; photoId: string }> {
  let signed = await presign();
  try {
    await uploadToR2(signed.uploadUrl, file, onProgress);
  } catch (err) {
    if (!(err instanceof Error) || !/HTTP 403/.test(err.message)) throw err;
    signed = await presign();
    await uploadToR2(signed.uploadUrl, file, onProgress);
  }
  return { key: signed.key, photoId: signed.photoId };
}
